﻿import React, { useState, MouseEvent } from 'react';
import BaseMenuItem from './BaseMenuItem';
import BaseTab from './BaseTab';
import Divider from '@mui/material/Divider';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as Features from '../../../../../../shared/Features';
import history from '../../../../../../shared/history';
import Menu from '@mui/material/Menu';
import { Translate } from '../../../../../../shared/LanguageService';
import { TranslatedLabels } from '../../../../../../shared/types';
import { useUserContext } from '../../../../../../shared/context/user-context';

type UsersTabProps = {
    label: string;
};

type TranslatedUsersMenuProps = {
    open: boolean;
    onClose: () => void;
    anchorElement: HTMLButtonElement | null;
};

type UsersMenuProps = {
    labels: TranslatedLabels;
} & TranslatedUsersMenuProps;

const InternalPersonalRoute = '/se360users';
const PhotographersRoute = '/photographers';
const PhotographerOverviewRoute = '/photographer-overview';
const ImageEditorsRoute = '/imageeditors';
const ImageEditorFirmsRoute = '/imageeditorfirms';
const PlanEditorsRoute = '/planeditors';
const PlanFirmsRoute = '/planfirms';
const StylingUsersRoute = '/stylingusers';
const StylingFirmsRoute = '/stylingfirms';
const CopywritersRoute = '/copywriters';

let routes = [
    InternalPersonalRoute,
    PhotographersRoute,
    PhotographerOverviewRoute,
    ImageEditorsRoute,
    ImageEditorFirmsRoute,
    PlanEditorsRoute,
    PlanFirmsRoute,
    StylingUsersRoute,
    StylingFirmsRoute,
    CopywritersRoute
];

function UsersMenu({ open, onClose, anchorElement, labels }: UsersMenuProps) {
    let { user } = useUserContext();

    let shouldShowListInternalStaff = Features.hasFeature(Features.ListSe360Users, user);
    let shouldShowViewPhotographers = Features.hasFeature(Features.ViewPhotographers, user);
    let shouldShowViewImageEditors = Features.hasFeature(Features.ViewImageEditors, user);
    let shouldShowViewImageEditorFirms = Features.hasFeature(Features.ViewImageEditorFirms, user);
    let shouldShowListPlanEditors = Features.hasFeature(Features.ListPlanEditors, user);
    let shouldShowListPlanFirms = Features.hasFeature(Features.ListPlanFirms, user);
    let shouldShowListStylingUsers = Features.hasFeature(Features.ListStylingUsers, user);
    let shouldShowListStylingFirms = Features.hasFeature(Features.ListStylingFirms, user);
    let shouldShowListCopywriters = Features.hasFeature(Features.ListCopywriters, user);

    let shouldShowDividerAfterInternalStaff =
        shouldShowListInternalStaff &&
        (shouldShowViewPhotographers ||
            shouldShowViewImageEditors ||
            shouldShowViewImageEditorFirms ||
            shouldShowListPlanEditors ||
            shouldShowListPlanFirms ||
            shouldShowListStylingUsers ||
            shouldShowListStylingFirms);

    let shouldShowDividerAfterPhotographers =
        shouldShowViewPhotographers &&
        (shouldShowViewImageEditors ||
            shouldShowViewImageEditorFirms ||
            shouldShowListPlanEditors ||
            shouldShowListPlanFirms ||
            shouldShowListStylingUsers ||
            shouldShowListStylingFirms);

    let shouldShowDividerAfterImageEditors =
        (shouldShowViewImageEditors || shouldShowViewImageEditorFirms) &&
        (shouldShowListPlanEditors ||
            shouldShowListPlanFirms ||
            shouldShowListStylingUsers ||
            shouldShowListStylingFirms);

    let shouldShowDividerAfterPlanEditors =
        (shouldShowListPlanEditors || shouldShowListPlanFirms) &&
        (shouldShowListStylingUsers || shouldShowListStylingFirms);

    let shouldShowDividerAfterStylists = shouldShowListCopywriters;

    function handleClickListInternalStaff() {
        onClose();
        history.push(InternalPersonalRoute);
    }

    function handleClickViewPhotographers() {
        onClose();
        history.push(PhotographersRoute);
    }

    function handleClickViewPhotographersOverview() {
        onClose();
        history.push(PhotographerOverviewRoute);
    }

    function handleClickViewImageEditors() {
        onClose();
        history.push(ImageEditorsRoute);
    }

    function handleClickViewImageEditorFirms() {
        onClose();
        history.push(ImageEditorFirmsRoute);
    }

    function handleClickListPlanEditors() {
        onClose();
        history.push(PlanEditorsRoute);
    }

    function handleClickListPlanFirms() {
        onClose();
        history.push(PlanFirmsRoute);
    }

    function handleClickListStylingUsers() {
        onClose();
        history.push(StylingUsersRoute);
    }

    function handleClickListStylingFirms() {
        onClose();
        history.push(StylingFirmsRoute);
    }

    function handleClickListCopywriters() {
        onClose();
        history.push(CopywritersRoute);
    }

    return (
        <Menu open={open} onClose={onClose} anchorEl={anchorElement}>
            {shouldShowListInternalStaff ? (
                <BaseMenuItem
                    route={InternalPersonalRoute}
                    onClick={handleClickListInternalStaff}
                    label={labels.listInternalStaff}
                />
            ) : null}
            {shouldShowDividerAfterInternalStaff ? <Divider /> : null}
            {shouldShowViewPhotographers ? (
                <BaseMenuItem
                    route={PhotographersRoute}
                    onClick={handleClickViewPhotographers}
                    label={labels.viewPhotographers}
                />
            ) : null}
            {shouldShowViewPhotographers ? (
                <BaseMenuItem
                    route={PhotographerOverviewRoute}
                    onClick={handleClickViewPhotographersOverview}
                    label={labels.viewPhotographersOverview}
                />
            ) : null}
            {shouldShowDividerAfterPhotographers ? <Divider /> : null}
            {shouldShowViewImageEditors ? (
                <BaseMenuItem
                    route={ImageEditorsRoute}
                    onClick={handleClickViewImageEditors}
                    label={labels.viewImageEditors}
                />
            ) : null}
            {shouldShowViewImageEditorFirms ? (
                <BaseMenuItem
                    route={ImageEditorFirmsRoute}
                    onClick={handleClickViewImageEditorFirms}
                    label={labels.viewImageEditorFirms}
                />
            ) : null}
            {shouldShowDividerAfterImageEditors ? <Divider /> : null}
            {shouldShowListPlanEditors ? (
                <BaseMenuItem
                    route={PlanEditorsRoute}
                    onClick={handleClickListPlanEditors}
                    label={labels.listPlanEditors}
                />
            ) : null}
            {shouldShowListPlanFirms ? (
                <BaseMenuItem
                    route={PlanFirmsRoute}
                    onClick={handleClickListPlanFirms}
                    label={labels.listPlanFirms}
                />
            ) : null}
            {shouldShowDividerAfterPlanEditors ? <Divider /> : null}
            {shouldShowListStylingUsers ? (
                <BaseMenuItem
                    route={StylingUsersRoute}
                    onClick={handleClickListStylingUsers}
                    label={labels.listStylingUsers}
                />
            ) : null}
            {shouldShowListStylingFirms ? (
                <BaseMenuItem
                    route={StylingFirmsRoute}
                    onClick={handleClickListStylingFirms}
                    label={labels.listStylingFirms}
                />
            ) : null}
            {shouldShowDividerAfterStylists ? <Divider /> : null}
            {shouldShowListCopywriters ? (
                <BaseMenuItem
                    route={CopywritersRoute}
                    onClick={handleClickListCopywriters}
                    label={labels.listCopywriters}
                />
            ) : null}
        </Menu>
    );
}

function TranslatedUsersMenu(props: TranslatedUsersMenuProps) {
    let labels: TranslatedLabels = {
        listInternalStaff: Translate('header/nav/se360-users'),
        viewPhotographers: Translate('header/nav/photographers'),
        viewPhotographersOverview: Translate('header/nav/photographer-overview'),
        viewImageEditors: Translate('header/nav/image-editors'),
        viewImageEditorFirms: Translate('header/nav/image-editor-firms'),
        listPlanEditors: Translate('header/nav/plan-editors'),
        listPlanFirms: Translate('header/nav/plan-firms'),
        listStylingUsers: Translate('header/nav/styling-users'),
        listStylingFirms: Translate('header/nav/styling-firms'),
        listCopywriters: Translate('header/nav/copywriters')
    };

    return <UsersMenu {...props} labels={labels} />;
}

function UsersTab({ label, ...props }: UsersTabProps) {
    let [showMenu, setShowMenu] = useState<boolean>(false);
    let [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);

    function handleClick(event: MouseEvent<HTMLButtonElement>) {
        setAnchorElement(event.currentTarget);
        setShowMenu((oldShowMenu) => !oldShowMenu);
    }

    function handleCloseMenu() {
        setShowMenu(false);
    }

    return (
        <>
            <BaseTab
                {...props}
                icon={
                    showMenu ? (
                        <ExpandLessIcon sx={{ width: '24px', height: '24px' }} />
                    ) : (
                        <ExpandMoreIcon sx={{ width: '24px', height: '24px' }} />
                    )
                }
                iconPosition="end"
                onClick={handleClick}
                label={label}
                sx={{ minHeight: '48px', height: '48px' }}
                route={null}
            />
            <TranslatedUsersMenu
                open={showMenu}
                anchorElement={anchorElement}
                onClose={handleCloseMenu}
            />
        </>
    );
}

function TranslatedUsersTab(props) {
    let label = Translate('header/nav/dropdown/user');
    return <UsersTab {...props} label={label} />;
}

TranslatedUsersTab.routes = routes;

export default TranslatedUsersTab;
